import { Routes } from '@angular/router';
import { CommonRoute } from '../common/enums/common-route.enum';
import { AccessControlGuard } from '../common/guards/access-control.guard';
import { SupportEnabledGuard } from './features/support-admin/guards/support-enabled.guard';
import { LoginGuard } from '../common/guards/login.guard';
import { LoginComponent } from './core/access-control/authentication/login/login.component';
import { LogoutComponent } from './core/access-control/authentication/logout/logout.component';
import { ResetPasswordComponent } from './core/access-control/authentication/new-password/reset-password.component';
import { RequestPasswordResetComponent } from './core/access-control/authentication/recover-password/request-password-reset.component';
import { RegistrationComponent } from './core/access-control/authentication/registration/registration.component';
import { VerifyEmailComponent } from './core/access-control/authentication/verify-email/verify-email.component';
import { AccessDeniedComponent } from './core/access-control/authorization/access-denied/access-denied.component';
import { NotFoundComponent } from './core/pages/not-found/not-found.component';
import { PublicLayoutComponent } from './layout/public.layout.component';

//Public pages (accessible without login)
const publicRoutes: Routes = [
  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      { path: '', pathMatch: 'full', redirectTo: '/login' },
      { path: 'login', component: LoginComponent, canActivate: [LoginGuard] },
      { path: 'logout', component: LogoutComponent },
      { path: 'sign-up', component: RegistrationComponent },
      { path: 'not-found', component: NotFoundComponent },
      {
        path: 'request-password-reset',
        component: RequestPasswordResetComponent,
      },
      { path: 'reset-password/:token', component: ResetPasswordComponent },
      { path: 'verify-email/:token', component: VerifyEmailComponent },
      {
        path: 'access-denied',
        pathMatch: 'full',
        component: AccessDeniedComponent,
      },
    ],
  },
];

// Protected area with layout and guards
const protectedRoutes: Routes = [
  {
    path: '',
    component: PublicLayoutComponent,
    canActivateChild: [AccessControlGuard],
    children: [
      {
        path: 'request-mfa-reset',
        loadComponent: () =>
          import('./core/access-control/authentication/request-mfa-reset/request-mfa-reset.component').then(
            (m) => m.RequestMfaResetComponent,
          ),
      },
      {
        path: 'mfa-setup',
        loadComponent: () =>
          import('./core/access-control/authentication/mfa-setup/mfa-setup.component').then(
            (m) => m.MfaSetupComponent,
          ),
      },
    ],
  },
  {
    path: '',
    canActivateChild: [AccessControlGuard],
    loadComponent: () =>
      import('./layout/app.layout.component').then((m) => m.AppLayoutComponent),
    children: [
      {
        path: 'home',
        pathMatch: 'full',
        loadComponent: () =>
          import('./core/pages/home/home.component').then(
            (m) => m.HomeComponent,
          ),
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./core/pages/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent,
          ),
      },

      {
        // Service tokens (API keys) — SUPER-ADMIN only (FRONTEND_URL +
        // COMPONENT permissions seeded to SUPER-ADMIN in db 00076). Hidden in
        // the menu unless granted; the screen 404s server-side when the
        // SERVICE_TOKENS_ENABLED master flag is off.
        path: 'service-tokens',
        loadComponent: () =>
          import('./core/access-control/service-tokens/service-tokens-admin.component').then(
            (m) => m.ServiceTokensAdminComponent,
          ),
      },
      {
        // SYSTEM catalog admin — SUPER-ADMIN only (FRONTEND_URL + COMPONENT
        // seeded to SUPER-ADMIN in db 00054).
        path: 'catalog',
        loadComponent: () =>
          import('./features/catalog/catalog-admin.component').then(
            (m) => m.CatalogAdminComponent,
          ),
      },
      {
        path: 'authorization',
        children: [
          {
            path: 'roles',
            loadComponent: () =>
              import('./core/access-control/authorization/roles/manage-roles.component').then(
                (m) => m.ManageRolesComponent,
              ),
          },
          {
            path: 'permissions',
            loadComponent: () =>
              import('./core/access-control/authorization/permissions/manage-permissions.component').then(
                (m) => m.ManagePermissionsComponent,
              ),
          },
          {
            // Buckets are first-class entities (BUCKETS table), not
            // Permission rows. Single MariaDB store (ADR-0049).
            path: 'buckets',
            loadComponent: () =>
              import('./features/storage-admin/buckets/manage-buckets.component').then(
                (m) => m.ManageBucketsComponent,
              ),
          },
          {
            // Per-bucket role mapping — writes BUCKET_ROLES, read by the
            // API's media-access check and upload gate.
            path: 'bucket-roles',
            loadComponent: () =>
              import('./features/storage-admin/bucket-roles/manage-bucket-roles.component').then(
                (m) => m.ManageBucketRolesComponent,
              ),
          },
          {
            path: 'users',
            loadComponent: () =>
              import('./core/access-control/authorization/users/manage-users.component').then(
                (m) => m.ManageUsersComponent,
              ),
          },
          {
            path: 'roles-to-user',
            loadComponent: () =>
              import('./core/access-control/authorization/roles-to-user/roles-to-user.component').then(
                (m) => m.RolesToUserComponent,
              ),
          },
          {
            path: 'permissions-to-role',
            loadComponent: () =>
              import('./core/access-control/authorization/permissions-to-role/permissions-to-role.component').then(
                (m) => m.PermissionsToRoleComponent,
              ),
          },
          {
            path: 'not-mapped-permissions',
            loadComponent: () =>
              import('./core/access-control/authorization/not-mapped-permissions/not-mapped-permissions.component').then(
                (m) => m.NotMappedPermissionsComponent,
              ),
          },
          {
            path: 'not-mapped-users',
            loadComponent: () =>
              import('./core/access-control/authorization/not-mapped-users/not-mapped-users.component').then(
                (m) => m.NotMappedUsersComponent,
              ),
          },
          {
            path: 'not-mapped-roles-to-users',
            loadComponent: () =>
              import('./core/access-control/authorization/not-mapped-roles-to-users/not-mapped-roles-to-users.component').then(
                (m) => m.NotMappedRolesToUsersComponent,
              ),
          },
          {
            path: 'not-mapped-roles-to-permissions',
            loadComponent: () =>
              import('./core/access-control/authorization/not-mapped-roles-to-permissions/not-mapped-roles-to-permissions.component').then(
                (m) => m.NotMappedRolesToPermissionsComponent,
              ),
          },
          // Dev-only utility — see test-tokens.component.ts. The api endpoint
          // it calls is hard-disabled when ENVIRONMENT=Production.
          {
            path: 'test-tokens',
            loadComponent: () =>
              import('./core/access-control/dev-tools/test-tokens/test-tokens.component').then(
                (m) => m.TestTokensComponent,
              ),
          },
        ],
      },
      {
        path: 'reference-data',
        children: [
          {
            path: 'data-types',
            loadComponent: () =>
              import('./features/reference-data/data-type/reference-data-type.component').then(
                (m) => m.ReferenceDataTypeComponent,
              ),
          },
          {
            path: 'data',
            loadComponent: () =>
              import('./features/reference-data/data/reference-data.component').then(
                (m) => m.ReferenceDataComponent,
              ),
          },
          {
            path: 'test-selector',
            loadComponent: () =>
              import('./features/reference-data/test-selector.component').then(
                (m) => m.TestSelectorComponent,
              ),
          },
        ],
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./core/pages/profile/profile.component').then(
            (m) => m.ProfileComponent,
          ),
      },
      {
        path: 'change-password',
        loadComponent: () =>
          import('./core/pages/change-password/change-password.component').then(
            (m) => m.ChangePasswordComponent,
          ),
      },
      {
        path: 'audit-trail',
        loadComponent: () =>
          import('./core/pages/audit-trail/audit-trail.component').then(
            (m) => m.AuditTrailComponent,
          ),
      },
      {
        path: 'scheduled-jobs',
        loadComponent: () =>
          import('./core/pages/scheduled-jobs/scheduled-jobs.component').then(
            (m) => m.ScheduledJobsComponent,
          ),
      },
      {
        // Review-before-publish for community listings. The API 404s the
        // underlying routes while MARKETPLACE_ENABLED is off, so the screen is
        // reachable but empty until publishing is switched on.
        path: 'marketplace-review',
        loadComponent: () =>
          import('./features/marketplace-review/marketplace-review.component').then(
            (m) => m.MarketplaceReviewComponent,
          ),
      },
      {
        path: 'marketplace-reports',
        loadComponent: () =>
          import('./features/marketplace-reports/marketplace-reports.component').then(
            (m) => m.MarketplaceReportsComponent,
          ),
      },
      {
        path: 'app-version',
        loadComponent: () =>
          import('./core/app-version/app-version.component').then(
            (m) => m.AppVersionComponent,
          ),
      },
      {
        path: 'country-availability',
        loadComponent: () =>
          import('./core/country-availability/country-availability.component').then(
            (m) => m.CountryAvailabilityComponent,
          ),
      },
      {
        path: 'organization',
        children: [
          {
            path: 'pending',
            loadComponent: () =>
              import('./features/organizations/pending/organization-pending.component').then(
                (m) => m.OrganizationPendingComponent,
              ),
          },
          {
            path: 'approved',
            loadComponent: () =>
              import('./features/organizations/approved/organization-approved.component').then(
                (m) => m.OrganizationApprovedComponent,
              ),
          },
          {
            path: 'rejected',
            loadComponent: () =>
              import('./features/organizations/rejected/organization-rejected.component').then(
                (m) => m.OrganizationRejectedComponent,
              ),
          },
          {
            path: 'suspended',
            loadComponent: () =>
              import('./features/organizations/suspended/organization-suspended.component').then(
                (m) => m.OrganizationSuspendedComponent,
              ),
          },
        ],
      },
      {
        path: 'payment-plans',
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'plans',
          },
          {
            path: 'plans',
            loadComponent: () =>
              import('./features/billing-admin/payment-plans/payment-plans-list/payment-plans-list.component').then(
                (m) => m.PaymentPlansListComponent,
              ),
          },
          {
            path: 'new',
            loadComponent: () =>
              import('./features/billing-admin/payment-plans/payment-plan-editor/payment-plan-editor.component').then(
                (m) => m.PaymentPlanEditorComponent,
              ),
          },
          {
            path: 'role-to-plan',
            loadComponent: () =>
              import('./features/billing-admin/payment-plans/payment-plans-role-mapping/payment-plans-role-mapping.component').then(
                (m) => m.PaymentPlansRoleMappingComponent,
              ),
          },
          {
            path: 'plan-to-roles',
            loadComponent: () =>
              import('./features/billing-admin/payment-plans/plan-role-mapping/plan-role-mapping.component').then(
                (m) => m.PlanRoleMappingComponent,
              ),
          },
          // `:id` is the catch-all editor route — MUST stay last so the static
          // children above (plans/new/role-to-plan/plan-to-roles) win.
          {
            path: ':id',
            loadComponent: () =>
              import('./features/billing-admin/payment-plans/payment-plan-editor/payment-plan-editor.component').then(
                (m) => m.PaymentPlanEditorComponent,
              ),
          },
        ],
      },
      {
        path: 'orders',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/billing-admin/admin-orders/admin-orders-list/admin-orders-list.component').then(
                (m) => m.AdminOrdersListComponent,
              ),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/billing-admin/admin-orders/admin-order-detail/admin-order-detail.component').then(
                (m) => m.AdminOrderDetailComponent,
              ),
          },
        ],
      },
      {
        path: 'refunds',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/billing-admin/refunds/refunds-list/refunds-list.component').then(
                (m) => m.RefundsListComponent,
              ),
          },
          {
            path: 'initiate/:id',
            loadComponent: () =>
              import('./features/billing-admin/refunds/refund-initiate/refund-initiate.component').then(
                (m) => m.RefundInitiateComponent,
              ),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/billing-admin/refunds/refund-detail/refund-detail.component').then(
                (m) => m.RefundDetailComponent,
              ),
          },
        ],
      },
      {
        path: 'refund-policies',
        loadComponent: () =>
          import('./features/billing-admin/refund-policies/refund-policies.component').then(
            (m) => m.RefundPoliciesComponent,
          ),
      },
      // Disputes / chargebacks (ADR-0134) — read-only, Stripe-only.
      {
        path: 'disputes',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/billing-admin/disputes/disputes-list/disputes-list.component').then(
                (m) => m.DisputesListComponent,
              ),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/billing-admin/disputes/dispute-detail/dispute-detail.component').then(
                (m) => m.DisputeDetailComponent,
              ),
          },
        ],
      },
      // In-app support inbox (ADR-0212). The queue plus a per-ticket thread; the
      // detail route has its own permission row because the guard normalises a
      // uuid segment to ':id' before matching.
      {
        path: 'support',
        // Master switch first: while support is off these routes must not
        // resolve at all, whatever the caller's permissions say.
        canActivate: [SupportEnabledGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './features/support-admin/support-queue/support-queue.component'
              ).then((m) => m.SupportQueueComponent),
          },
          {
            path: ':id',
            loadComponent: () =>
              import(
                './features/support-admin/support-ticket-detail/support-ticket-detail.component'
              ).then((m) => m.SupportTicketDetailComponent),
          },
        ],
      },
      // Payment-ops console (ADR-0022).
      {
        path: 'subscriptions',
        loadComponent: () =>
          import('./features/billing-admin/payment-ops/subscriptions-admin.component').then(
            (m) => m.SubscriptionsAdminComponent,
          ),
      },
      {
        path: 'subscriptions/:userPlanId',
        loadComponent: () =>
          import('./features/billing-admin/payment-ops/subscription-detail.component').then(
            (m) => m.SubscriptionDetailComponent,
          ),
      },
      {
        path: 'customers',
        loadComponent: () =>
          import('./features/billing-admin/payment-ops/customers-admin.component').then(
            (m) => m.CustomersAdminComponent,
          ),
      },
      {
        path: 'complimentary',
        loadComponent: () =>
          import('./features/billing-admin/payment-ops/complimentary-admin.component').then(
            (m) => m.ComplimentaryAdminComponent,
          ),
      },
      {
        path: 'payment-events',
        loadComponent: () =>
          import('./features/billing-admin/payment-ops/payment-events-admin.component').then(
            (m) => m.PaymentEventsAdminComponent,
          ),
      },
      // Drift candidates + targeted/bulk provider re-sync (ADR-0059,
      // SUPER-ADMIN — db 00083).
      {
        path: 'payment-reconciliation',
        loadComponent: () =>
          import(
            './features/billing-admin/payment-ops/payment-reconciliation-admin.component'
          ).then((m) => m.PaymentReconciliationAdminComponent),
      },
      // Webhook/notification plumbing verification, all envs (SUPER-ADMIN —
      // db 00085).
      {
        path: 'callback-health',
        loadComponent: () =>
          import(
            './features/billing-admin/payment-ops/callback-health-admin.component'
          ).then((m) => m.CallbackHealthAdminComponent),
      },
      // Non-prod Test Data console — per-user purge, env sweeps and the
      // whole-environment Factory Reset on one screen (ADR-0062 + ADR-0077,
      // merged by ADR-0089 — api routes 404 in production; RBAC seeds only
      // exist off-prod, db 00119 + 00125).
      {
        path: 'factory-reset',
        loadComponent: () =>
          import('./features/test-data/factory-reset.component').then(
            (m) => m.FactoryResetComponent,
          ),
      },
      // Payment Scenario Simulator (SUPER-ADMIN, non-prod only — ADR-0112:
      // api routes 404 in production; RBAC seeds only exist off-prod, db 00147).
      {
        path: 'payment-simulator',
        loadComponent: () =>
          import('./features/payment-simulator/payment-simulator.component').then(
            (m) => m.PaymentSimulatorComponent,
          ),
      },
      // Finance overview, the first page of the Finance menu (SUPER-ADMIN - db 00238).
      {
        path: 'finance-overview',
        loadComponent: () =>
          import('./features/billing-admin/finance/overview/finance-overview.component').then(
            (m) => m.FinanceOverviewComponent,
          ),
      },
      // Finance screens (SUPER-ADMIN — db 00082).
      {
        path: 'finance-ledger',
        loadComponent: () =>
          import('./features/billing-admin/finance/finance-ledger-admin.component').then(
            (m) => m.FinanceLedgerAdminComponent,
          ),
      },
      {
        path: 'finance-ledger/:id',
        loadComponent: () =>
          import('./features/billing-admin/finance/finance-ledger-detail.component').then(
            (m) => m.FinanceLedgerDetailComponent,
          ),
      },
      {
        path: 'finance-crosscheck',
        loadComponent: () =>
          import('./features/billing-admin/finance/finance-crosscheck.component').then(
            (m) => m.FinanceCrosscheckComponent,
          ),
      },
      // Finance reports (SUPER-ADMIN — db 00106).
      {
        path: 'finance-reports',
        loadComponent: () =>
          import('./features/billing-admin/finance/finance-reports.component').then(
            (m) => m.FinanceReportsComponent,
          ),
      },
      // Gateway fees config (SUPER-ADMIN — db 00107).
      {
        path: 'gateway-fees',
        loadComponent: () =>
          import('./features/billing-admin/gateway-fees/gateway-fees.component').then(
            (m) => m.GatewayFeesComponent,
          ),
      },
      // Finance/ops alerts (SUPER-ADMIN — db 00110).
      {
        path: 'alerts',
        loadComponent: () =>
          import('./features/billing-admin/alerts/alerts.component').then(
            (m) => m.AlertsComponent,
          ),
      },
      // Metrics & analytics (SUPER-ADMIN — db 00111).
      {
        path: 'metrics',
        loadComponent: () =>
          import('./features/billing-admin/metrics/metrics.component').then(
            (m) => m.MetricsComponent,
          ),
      },
      // Payouts + month-end close (SUPER-ADMIN — db 00112).
      {
        path: 'payouts',
        loadComponent: () =>
          import('./features/billing-admin/payouts/payouts.component').then(
            (m) => m.PayoutsComponent,
          ),
      },
    ],
  },
];

export const routes: Routes = [
  ...publicRoutes,
  ...protectedRoutes,
  { path: '**', redirectTo: CommonRoute.NOT_FOUND }, // fallback
];
